// ── BlockAdder.tsx ────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  X,
  Component as ComponentIcon,
  Type,
  Heading2,
  Image,
  Quote,
  Lightbulb,
  Minus,
  MousePointerClick,
  Columns2,
  Code2,
  SquareStack,
  ChevronsDownUp,
  BarChart3,
  ListOrdered,
  PenLine,
  Table,
  type LucideIcon,
  KeyRound,
  CircleUser,
  ListChecks,
} from 'lucide-react';
import { useEditorStore } from '../stores/editorStore.js';
import { api } from '../hooks/api.js';
import { useDocumentKind } from './editor/DocumentKindContext.js';
import type { BlockParent, DragPath } from '../types.js';
import type { ComponentTarget } from '@blogforge/shared';

const BLOCK_TYPES: { type: string; label: string; icon: LucideIcon }[] = [
  { type: 'paragraph', label: 'Paragraph', icon: Type },
  { type: 'heading', label: 'Heading', icon: Heading2 },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'avatar', label: 'Avatar', icon: CircleUser },
  { type: 'quote', label: 'Quote', icon: Quote },
  { type: 'callout', label: 'Callout', icon: Lightbulb },
  { type: 'divider', label: 'Divider', icon: Minus },
  { type: 'button', label: 'Button', icon: MousePointerClick },
  { type: 'columns', label: '2 Columns', icon: Columns2 },
  { type: 'html', label: 'HTML', icon: Code2 },
  { type: 'section', label: 'Section', icon: SquareStack },
  { type: 'accordion', label: 'Accordion', icon: ChevronsDownUp },
  { type: 'stat', label: 'Stat', icon: BarChart3 },
  { type: 'table', label: 'Table', icon: Table },
  { type: 'steps', label: 'Steps', icon: ListOrdered },
  { type: 'signature', label: 'Signature', icon: PenLine },
  { type: 'otp', label: 'OTP code', icon: KeyRound },
  { type: 'list', label: 'List', icon: ListChecks },
];

interface BlockAdderProps {
  parent?: BlockParent;
  path?: DragPath;
  /**
   * Document kind we are adding to. When known, the adder also offers reusable
   * components whose declared target includes this kind. Defaults to the
   * canvas's kind (DocumentKindContext), so nested adders need not pass it;
   * the component designer has none (no component-in-component).
   */
  contentKind?: 'post' | 'email' | undefined;
}

interface ComponentRow {
  id: string;
  name: string;
  target: ComponentTarget;
  version: number;
  blocks: string;
}

export function BlockAdder({ parent, path, contentKind: contentKindProp }: BlockAdderProps) {
  const canvasKind = useDocumentKind();
  const contentKind = contentKindProp ?? canvasKind;
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const addBlock = useEditorStore((s) => s.addBlock);
  const addBlockToSection = useEditorStore((s) => s.addBlockToSection);
  const addBlockToPath = useEditorStore((s) => s.addBlockToPath);
  const insertComponent = useEditorStore((s) => s.insertComponent);

  // A list is offered where it can work: not inside another list's item
  // template (a list's fields are values, so a list of lists has nothing to
  // bind to), and not in a post, which has no variable definitions at all —
  // see the note on the block's empty web renderer in the theme registry.
  const insideList =
    parent?.type === 'list' || (path ?? []).some((segment) => segment.type === 'list');

  // Reusable components go anywhere a block list can grow — the root, a
  // section, a column, an accordion item — but not into a list's item
  // template, which holds exactly one block.
  const showComponents = !!contentKind && !insideList;

  const { data: componentsData } = useQuery({
    queryKey: ['components', { limit: 100, target: undefined }],
    queryFn: () => api.components.list({ page: 1, limit: 100 }),
    enabled: showComponents,
  });
  const components: ComponentRow[] =
    (componentsData as { data?: ComponentRow[] } | undefined)?.data ?? [];

  const offerableBlocks = BLOCK_TYPES.filter(({ type }) => {
    if (type !== 'list') return true;
    return contentKind !== 'post' && !insideList;
  });

  const allowedComponents = components.filter((c) =>
    contentKind === 'email'
      ? c.target === 'email' || c.target === 'both'
      : c.target === 'post' || c.target === 'both',
  );

  const add = (type: string) => {
    const defaults: Record<string, object> = {
      paragraph: { content: '', attrs: {} },
      heading: { content: 'New heading', attrs: { level: 2 } },
      image: { content: '', attrs: { src: '', alt: '' } },
      quote: { content: 'Quote text', attrs: {} },
      callout: { content: 'Callout text', attrs: { variant: 'info' } },
      divider: { content: '', attrs: {} },
      button: { content: 'Click here', attrs: { href: '#', target: '_self', variant: 'primary' } },
      columns: {
        content: '',
        attrs: { shouldWrapOnMobile: true, columns: 2, gap: '2rem', columnBlocks: [[], []] },
      },
      html: { content: '<p>Custom HTML</p>', attrs: {} },
      section: {
        content: '',
        attrs: {
          backgroundType: 'color',
          backgroundSrc: undefined,
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
          backgroundFixed: false,
          overlayColor: undefined,
          minHeight: undefined,
          maxWidth: undefined,
          verticalAlign: 'center',
          paddingX: '2rem',
          paddingY: '4rem',
          blocks: [],
        },
      },
      accordion: {
        content: '',
        attrs: {
          allowMultiple: false,
          items: [],
        },
      },
      stat: {
        content: '',
        attrs: {
          stats: [],
          // Set explicitly: blocks are built here as raw objects (never
          // zod-parsed), so the schema's .default()s do not apply and the
          // renderer would otherwise see these as undefined.
          layout: 'row',
          animateOnScroll: true,
        },
      },
      table: {
        content: '',
        attrs: {
          // Same reason as `stat` above: every attr is spelled out. Two
          // starter columns and rows, so a freshly added table looks like a
          // table instead of an empty dashed box.
          headers: ['Feature', 'Free', 'Pro'],
          rows: [
            ['Seats', '1', 'Unlimited'],
            ['Support', 'Community', 'Priority'],
          ],
          footerRows: [['', '$0', '$29 / mo']],
          headerColumn: false,
          caption: undefined,
          captionPosition: 'top',
          striped: true,
          bordered: false,
          stickyHeader: false,
          scrollable: true,
          density: 'normal',
          verticalAlign: 'middle',
          align: ['left', 'center', 'center'],
          columnWidths: ['auto', '120px', '120px'],
          colors: {},
        },
      },
      steps: {
        content: '',
        attrs: {
          steps: [],
          layout: 'vertical',
          showNumbers: true,
          showConnectors: true,
          connectorType: 'line',
          stepSize: 'md',
        },
      },
      signature: {
        content: '',
        attrs: {
          name: 'Jane Doe',
          title: '',
          company: '',
          avatarSrc: '',
          avatarAlt: '',
          logoSrc: '',
          logoAlt: '',
          socials: [],
          variant: 'compact',
          layout: 'left',
        },
      },
      avatar: {
        content: '',
        attrs: {
          image: { src: '', srcIsMustache: false, alt: '', skipOptimize: false },
          size: 64,
          radius: '50%',
          hrefIsMustache: false,
        },
        style: { textAlign: 'center' },
      },
      otp: {
        content: '',
        attrs: {
          code: '',
          codeIsMustache: false,
          length: 6,
          groupSize: 3,
          label: 'Your verification code',
          boxed: true,
        },
      },
      // Bound to nothing yet: the inspector picks the list variable, and the
      // canvas says so until it is picked.
      list: {
        attrs: {
          dataBindingKey: '',
          dividerType: 'none',
        },
      },
    };

    const block = { type, ...defaults[type] } as never;

    if (parent) {
      if (parent.type === 'columns' && path) {
        // Inside a columns container: append to the exact column list.
        addBlockToPath(block, [...path, parent]);
      } else if (parent.type === 'list' && path) {
        // A list holds one item template, which the path resolves to — the same
        // way a column is named by its segment.
        addBlockToPath(block, [...path, parent]);
      } else if (parent.isSectionInAccordion && path) {
        addBlockToPath(block, [...path, parent]);
      } else if (parent.type === 'section' && path && path.length > 0) {
        // A section that is not at the root — inside a list's item template,
        // another section or a column. `addBlockToSection` resolves the parent
        // by id in the *root* list, which never finds it, so the path is what
        // names it (the section's own ancestors plus the section itself).
        addBlockToPath(block, [...path, parent]);
      } else {
        addBlockToSection(block, parent);
      }
    } else {
      addBlock(block);
    }
    setOpen(false);
  };

  // Escape closes the picker, as it does every other dialog.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const insert = (component: ComponentRow) => {
    let blocks: unknown[] = [];
    try {
      blocks = JSON.parse(component.blocks);
    } catch {
      blocks = [];
    }
    // Nested adders name their container as the last path segment, the same
    // path `add` hands addBlockToPath.
    insertComponent(
      { id: component.id, version: component.version },
      blocks as never[],
      parent ? [...(path ?? []), parent] : [],
    );
    setOpen(false);
    void qc.invalidateQueries({ queryKey: ['components'] });
  };

  // The picker is portaled to <body>: an adder can sit deep inside a narrow
  // container (a column, a section in an accordion), and the canvas's own
  // transforms and document styles would otherwise clip or rescale it. React
  // still bubbles portal events up the component tree, so they stop here
  // rather than reaching the parent block's select and drag handlers.
  const modal = (
    <div
      className="modal-backdrop p-0 sm:p-5"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(false);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add a block"
        onClick={(e) => e.stopPropagation()}
        className="modal-card flex h-full max-h-none flex-col overflow-y-auto rounded-none border-0 sm:h-auto sm:max-h-[85vh] sm:max-w-2xl sm:rounded-[var(--radius-card)] sm:border"
      >
        <div className="border-line flex shrink-0 items-center justify-between border-b px-5 py-4">
          <h2 className="text-ink text-[15px] font-bold tracking-tight">Add a block</h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1"
          >
            <X size={17} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {offerableBlocks.map(({ type, label, icon: Icon }) => (
              <button
                type="button"
                key={type}
                onClick={() => add(type)}
                className="group border-line bg-bg-soft hover:border-accent-border flex items-center gap-2.5 rounded-xl border px-2.5 py-2.5 text-left transition-all duration-150 hover:-translate-y-px hover:shadow-sm"
              >
                <span className="bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-ink flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors">
                  <Icon size={13} strokeWidth={2} />
                </span>
                <span className="text-ink-soft group-hover:text-ink truncate text-xs font-semibold transition-colors">
                  {label}
                </span>
              </button>
            ))}
          </div>

          {showComponents && (
            <div className="border-line mt-4 border-t pt-3.5">
              <p className="text-muted mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase">
                <ComponentIcon size={11} />
                Reusable components
              </p>
              {allowedComponents.length === 0 ? (
                <p className="border-line text-muted rounded-xl border border-dashed px-3 py-3 text-xs">
                  No components available here yet — save one from the editor toolbar.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {allowedComponents.map((c) => {
                    let blockCount = 0;
                    try {
                      blockCount = (JSON.parse(c.blocks) as unknown[]).length;
                    } catch {
                      blockCount = 0;
                    }
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => insert(c)}
                        className="group border-line bg-bg-soft hover:border-accent-border flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 hover:-translate-y-px hover:shadow-sm"
                      >
                        <span className="bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-ink flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors">
                          <ComponentIcon size={13} strokeWidth={2} />
                        </span>
                        <span className="text-ink-soft group-hover:text-ink min-w-0 flex-1 truncate text-xs font-semibold transition-colors">
                          {c.name}
                        </span>
                        <span className="text-muted hidden flex-none text-[10px] sm:block">
                          {blockCount} block{blockCount === 1 ? '' : 's'}
                        </span>
                        <span className="chip shrink-0 px-2 py-0.5 text-[9.5px] tracking-wide uppercase">
                          {c.target === 'both'
                            ? 'Posts & emails'
                            : c.target === 'post'
                              ? 'Posts'
                              : 'Emails'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border-line-strong/70 text-muted hover:border-accent-border hover:bg-accent/5 hover:text-accent flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed px-3 py-2.5 text-[13px] font-semibold transition-all duration-150"
      >
        <span className="bg-accent/10 text-accent flex h-5 w-5 items-center justify-center rounded-full">
          <Plus size={12} strokeWidth={2.6} />
        </span>
        Add block
      </button>
      {open && createPortal(modal, document.body)}
    </div>
  );
}
